<script lang="ts" setup>
// ============================================================================
// RawSeriesChart (DEV-595, C3) — graphe d'un jeu de séries dataV2 HOMOGÈNES
// (même unité), avec COULEUR DE QUALITÉ par point (code `t`). Réutilise le
// Plotly maison du module + le mapping/légende de qualité (C2). Le regroupement
// par unité (énergie/volumes vs coefficients) est décidé par la page (P1) ; ce
// composant trace un groupe cohérent.
// ============================================================================
import { computed, ref } from "vue"
import { quality, type ProvenanceCode } from "../quality/dataQuality"
import DataQualityLegend from "../quality/DataQualityLegend.vue"
import PlotlyChart from "@modules/data/components/PlotlyChart.vue"
import {
    finalizeHomeysPlotlyGraph,
    type HomeysPlotlyChartMeta,
} from "@modules/data/utils/plotly/homeysPlotlyChart"

/** Une série prête à tracer (colonnaire, cf. route dataV2). */
export type RawSeries = {
    variable: string
    label: string
    unit: string
    freq: "daily" | "periode" | "point"
    /** dates simples (daily/point) ou paires [from,to] (periode) */
    x: Array<string | [string, string]>
    v: Array<number | null>
    t: ProvenanceCode[]
}

const props = withDefaults(
    defineProps<{ series: RawSeries[]; height?: number }>(),
    { height: 320 }
)

// --- Toggle de série : multi-séries → on affiche UNE série à la fois (plus
//     lisible que des barres groupées) ; on compare en switchant. ---------
const selectedIndex = ref<number>(0)
const hasToggle = computed<boolean>(() => props.series.length > 1)

/** Options du sélecteur de série (pattern maison : `[{name, value}]`). */
const seriesOptions = computed(() =>
    props.series.map((s, i) => ({ name: s.label, value: i }))
)

/** Séries affichées + leur index d'ORIGINE (pour garder une couleur stable). */
const displayed = computed<Array<{ s: RawSeries; idx: number }>>(() => {
    if (!hasToggle.value) return props.series.map((s, idx) => ({ s, idx }))
    const i = Math.min(selectedIndex.value, props.series.length - 1)
    return [{ s: props.series[i], idx: i }]
})

/** x → date unique : fin de période pour `periode`, sinon la date elle-même. */
function xDate(x: string | [string, string]): string {
    return Array.isArray(x) ? x[1] : x
}
/** Centre d'une période (pour positionner la barre), sinon la date. */
function xCenter(x: string | [string, string]): string {
    if (!Array.isArray(x)) return x
    const a = Date.parse(x[0])
    const b = Date.parse(x[1])
    return new Date((a + b) / 2).toISOString()
}
/** Largeur de barre = durée de la période en ms (les barres COUVRENT la période,
 *  juxtaposées) ; undefined pour une date simple (barre standard). */
function widthMs(x: string | [string, string]): number | undefined {
    return Array.isArray(x)
        ? Math.max(Date.parse(x[1]) - Date.parse(x[0]), 0)
        : undefined
}
/** ISO (YYYY-MM-DD…) → JJ/MM/AAAA (pour le tooltip des périodes). */
function fmtFr(iso: string): string {
    const [y, m, d] = iso.slice(0, 10).split("-")
    return `${d}/${m}/${y}`
}

const unit = computed<string>(() => displayed.value[0]?.s.unit ?? "")

/**
 * Traces brutes (le hover standard maison est appliqué ensuite). Convention :
 *  • COULEUR = QUALITÉ par point (vert publiée·définitive / ambre provisoire /
 *    gris informatif) — lisible. Le sélecteur affichant 1 série à la fois, la
 *    couleur peut porter la qualité sans conflit d'identité de série.
 *  • barres pour les grandeurs de période ; index (ponctuel) = ligne + points.
 */
const rawTraces = computed<object[]>(() =>
    displayed.value.map(({ s }) => {
        const colors = s.t.map((code) => quality(code).plot)
        if (s.freq === "point") {
            return {
                type: "scatter",
                mode: "lines+markers",
                name: s.label,
                x: s.x.map(xDate),
                y: s.v,
                line: { color: "#cbd5e1", width: 1.5 },
                marker: {
                    size: 9,
                    color: colors,
                    line: { width: 1, color: "#ffffff" },
                },
            }
        }
        const widths = s.x.map(widthMs)
        const hasWidth = widths.some((w) => typeof w === "number" && w > 0)
        return {
            type: "bar",
            name: s.label,
            x: s.x.map(xCenter),
            y: s.v,
            // barres couvrant la période (juxtaposées) quand x = [from,to]
            ...(hasWidth ? { width: widths } : {}),
            // liseré blanc seulement pour les barres-PÉRIODE (juxtaposées, à
            // séparer visuellement) ; sur les barres journalières denses le
            // liseré se chevauche et brouille la lecture → pas de bordure.
            marker: {
                color: colors,
                ...(hasWidth ? { line: { width: 1, color: "#ffffff" } } : {}),
            },
        }
    })
)

/** Layout partiel (marges/légende/barres) — enrichi par le helper maison. */
const rawLayout = computed<Record<string, unknown>>(() => ({
    margin: { t: 16, r: 16, b: 56, l: 56 },
    xaxis: { type: "date", showgrid: false },
    yaxis: { zeroline: false },
    showlegend: false,
    barmode: "group",
    bargap: 0.25,
}))

/** Méta chart pour le pipeline maison (unité du hover via y_label ; pas de titre X). */
const chartMeta = computed<HomeysPlotlyChartMeta>(
    () =>
        ({
            name: "data_grdf",
            x_label: "",
            y_label: unit.value,
        }) as HomeysPlotlyChartMeta
)

/**
 * Graphe finalisé via le pipeline maison : hover DAPI standard (date + Y + unité)
 * + layout/axes/légende uniformes → homogène avec le reste de l'app.
 * Pour les barres-PÉRIODE, on SURCHARGE ensuite le hover : la valeur n'est pas
 * ponctuelle mais définie sur un intervalle → on affiche `début → fin` (les 2
 * bornes) au lieu de la seule date centrale. Daily/point gardent le hover maison.
 */
const graph = computed(() => {
    const g = finalizeHomeysPlotlyGraph(
        { data: rawTraces.value, layout: rawLayout.value },
        chartMeta.value
    )
    // Survol "closest" : le tooltip s'accroche au RECTANGLE / point survolé, pas
    // au "x unified" maison (qui afficherait une date d'axe centrale dénuée de
    // sens — la valeur est sur une période/un point — + une ligne verticale).
    const layout = g.layout as Record<string, unknown>
    layout.hovermode = "closest"
    const xaxis = (layout.xaxis ?? {}) as Record<string, unknown>
    xaxis.showspikes = false
    layout.xaxis = xaxis
    displayed.value.forEach(({ s }, i) => {
        const tr = g.data[i] as Record<string, unknown>
        const unitPart = s.unit?.trim() ? ` ${s.unit.trim()}` : ""
        if (s.freq === "periode") {
            // période : les 2 bornes (début → fin)
            tr.customdata = s.x.map((x) =>
                Array.isArray(x)
                    ? [fmtFr(x[0]), fmtFr(x[1])]
                    : [fmtFr(x), fmtFr(x)]
            )
            tr.hovertemplate =
                `<b>%{y:,.2~f}${unitPart}</b><br>` +
                `%{customdata[0]} → %{customdata[1]}<extra></extra>`
        } else {
            // daily / point : date du jour (ou du relevé)
            tr.customdata = s.x.map((x) => fmtFr(Array.isArray(x) ? x[0] : x))
            tr.hovertemplate = `<b>%{y:,.2~f}${unitPart}</b><br>%{customdata}<extra></extra>`
        }
    })
    return g
})

/**
 * Signature de la série affichée → clé du PlotlyChart. Dès que les données
 * changent (variable, grain, nombre de points, bornes de la fenêtre), la clé
 * change → Vue REMONTE le PlotlyChart → `Plotly.newPlot` PROPRE au lieu d'un
 * `Plotly.animate` qui morphe mal quand le nombre de barres varie (ex. passage
 * 3 mois → 1 an, ou changement de dates). Évite barres fantômes / largeurs
 * incohérentes. Les sélecteurs (Série / Vue) restent montés → état préservé.
 */
const plotKey = computed<string>(() => {
    const s = displayed.value[0]?.s
    if (!s || !s.x.length) return "empty"
    const enc = (x: string | [string, string]) =>
        Array.isArray(x) ? `${x[0]}_${x[1]}` : x
    return `${s.variable}|${s.freq}|${s.x.length}|${enc(s.x[0])}|${enc(
        s.x[s.x.length - 1]
    )}`
})

/** Codes provenance présents (sur la série affichée) → légende qualité. */
const codesPresent = computed<ProvenanceCode[]>(() => {
    const set = new Set<ProvenanceCode>()
    displayed.value.forEach(({ s }) => s.t.forEach((c) => set.add(c)))
    return Array.from(set).sort((a, b) => a - b)
})

const hasData = computed<boolean>(() =>
    props.series.some((s) => s.v.some((val) => val !== null))
)
</script>

<template>
    <div class="raw-series-chart">
        <template v-if="hasData">
            <!-- Sélecteur de série (multi-séries) : pattern maison BaseSelect -->
            <BaseFloatLabel
                v-if="hasToggle"
                variant="on"
                class="mb-3 min-w-56 max-w-xs"
            >
                <BaseSelect
                    id="raw-series-select"
                    class="w-full"
                    v-model="selectedIndex"
                    :options="seriesOptions"
                    option-label="name"
                    option-value="value"
                />
                <label for="raw-series-select">Série</label>
            </BaseFloatLabel>

            <div :style="{ height: `${height}px` }">
                <PlotlyChart :key="plotKey" :graph="graph" />
            </div>
            <DataQualityLegend :codes="codesPresent" class="mt-3" />
        </template>
        <div v-else class="text-center text-slate-400 py-8">
            Aucune donnée à tracer sur la période.
        </div>
    </div>
</template>
